import * as React from 'react';
import { parseCoachingMessage } from '../../voice-agent/utils/parseCoachingMessage';
import { clearCachedConversationId, getCachedConversationId, setCachedConversationId } from './conversationCache';
import { CoachMessage, CoachMessageRole, PendingKeywordTag } from './types';
import { CoachUpstreamMessage, useRealtimeCoaching } from './useRealtimeCoaching';

/**
 * Arguments for `useCoaching`. All fields are optional because the hook waits
 * for a `companyId` before bootstrapping; passing only `companyId` is enough
 * to start (or resume) a conversation for that lead.
 */
export type UseCoachingArgs = {
	companyId?: string;
	contactId?: string;
	contactName?: string;
	contactTitle?: string;
	phoneCallId?: string;
	leadServedId?: string;
};

export type UseCoachingApi = {
	messages: CoachMessage[];
	isResponding: boolean;
	isLoadingHistory: boolean;
	/** True if the active conversation was resumed from cache rather than freshly created. */
	wasResumed: boolean;
	/** Stable id for the active conversation, or null while bootstrapping. */
	conversationId: string | null;
	/**
	 * Send a composer message to the coach. When `tag` is provided, the next
	 * incoming assistant message is decorated with `pinnedKeyword` + `pinColor`
	 * so the UI can render the pinned-keyword ribbon.
	 */
	send(text: string, tag?: PendingKeywordTag): void;
	/** Append a finalized transcript entry to the conversation message stream. */
	addTranscriptEntry(entry: { id: string; speaker: string; text: string; timestamp: Date }): void;
	/**
	 * Enqueue an "ask the AI for a suggestion based on recent transcript"
	 * dispatch. Goes through the same send-queue as direct sends so it never
	 * overlaps an in-flight stream. No-op if no transcript has accumulated
	 * since the last suggestion / resume.
	 */
	requestAutoSuggestion(): void;
	/**
	 * Manually request a coaching suggestion based on accumulated transcript.
	 * Alias of {@link requestAutoSuggestion} surfaced for the "Get suggestion"
	 * button rendered when `coffeePreferences.autoSuggestionsEnabled === false`.
	 * Same queue + dedup semantics — at most one suggestion can be queued at a
	 * time.
	 */
	requestSuggestion(): void;
	/**
	 * Manually request the end-of-call summary. Mirrors {@link endCall} but
	 * skips the "transcript-required" guard — the user has explicitly clicked
	 * "Get summary" so we trust their intent. Routes through the same send
	 * queue so a concurrent in-flight stream (Game Plan, last suggestion) is
	 * allowed to finish first; deduped against any already-queued summary.
	 */
	requestPostCallSummary(): void;
	/**
	 * Drop the cached conversation for this lead and bootstrap a brand-new one.
	 * The next render of `useCoaching` will see no cached id and call
	 * `realtime.createNew` again, which re-fires the `before_call` keyword pins
	 * and clears the message stream. Use sparingly — this is the "start fresh"
	 * escape hatch from the center column header.
	 */
	startNewConversation(): void;
	/** Request the end-of-call summary and tear down the session. */
	endCall(): void;
};

const resolveRole = (message: CoachUpstreamMessage): CoachMessageRole => {
	if (message.role === 'assistant') return 'assistant';
	if (message.messageType === 'transcript') return 'transcript';
	return 'user';
};

const formatTime = (timestamp: Date): string =>
	timestamp.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const toCoachMessage = (message: CoachUpstreamMessage, tagForId: Map<string, PendingKeywordTag>): CoachMessage => {
	const role = resolveRole(message);
	const base: CoachMessage = {
		id: message.id,
		role,
		time: formatTime(message.timestamp),
		text: message.text,
		isStreaming: message.isStreaming || undefined,
	};

	if (role === 'assistant') {
		const parsed = parseCoachingMessage(message.text);
		if (parsed?.suggestion) base.sayThis = parsed.suggestion;
		if (parsed?.reasoning) base.why = parsed.reasoning;

		const tag = tagForId.get(message.id);
		if (tag) {
			base.pinnedKeyword = tag.pinnedKeyword;
			base.pinColor = tag.pinColor;
		}
	}

	return base;
};

/**
 * Focused-view coaching adapter. Owns conversation lifecycle (create / resume
 * / cache), keyword-tag claim queue, send-queue gate, and the upstream→view
 * message normalization. Wraps `useRealtimeCoaching` (the focused view's own
 * realtime hook) — the legacy `TysonCoachingContext` is no longer involved.
 *
 * Bootstrap policy:
 *   1. Wait for `companyId` to land.
 *   2. Look up `companyId → conversationId` in localStorage. If present,
 *      `resume()` the existing conversation and skip the create POST.
 *   3. Otherwise `createNew({ companyId, ... })`, cache the new id keyed by
 *      `companyId` so the next reload resumes.
 *   4. On 4xx during history backfill (stale cache), the caller should clear
 *      the cache entry and retry — handled by `useRealtimeCoaching`'s
 *      `historyQuery` failing silently; future iteration: surface this here.
 *
 * Send-queue gate and tag-claim semantics are unchanged from the prior
 * `useTysonCoaching`-backed implementation — see pins/README.md "End-to-end
 * flow" for the full picture.
 */
export function useCoaching(args: UseCoachingArgs): UseCoachingApi {
	const realtime = useRealtimeCoaching();

	// Each queued tag carries `skipIds`: the set of assistant-message ids that were
	// already in upstream at the moment the user clicked the keyword pin. Those
	// pre-existing messages are not the reply to this send and must not be claimed,
	// even if they happen to be untagged. See pins/README.md "Why snapshots".
	type QueuedTag = PendingKeywordTag & { skipIds: Set<string> };
	type QueueItem = { kind: 'direct'; text: string; tag?: QueuedTag } | { kind: 'autoSuggestion' } | { kind: 'summary' };
	const pendingTagsRef = React.useRef<QueuedTag[]>([]);
	const tagByIdRef = React.useRef<Map<string, PendingKeywordTag>>(new Map());
	const sendQueueRef = React.useRef<QueueItem[]>([]);
	const inFlightRef = React.useRef(false);
	const upstreamRef = React.useRef(realtime.coachingMessages);
	const [tagVersion, setTagVersion] = React.useState(0);
	// Track which (companyId, contactId) pair we've already auto-bootstrapped
	// for so we don't fire two conversations on re-renders.
	const startedKeyRef = React.useRef<string | null>(null);
	// Tracks the last `companyId` we bootstrapped for. When the rep switches
	// leads (Search → click result, Next lead, inbound CallFromUnknown
	// attach), this lets the bootstrap effect detect the company change and
	// reset the conversation — otherwise the `realtime.conversationId` guard
	// below would keep the panel pinned to the previous lead's conversation.
	const lastCompanyIdRef = React.useRef<string | null>(null);

	const upstream = realtime.coachingMessages;
	upstreamRef.current = upstream;

	// Auto-bootstrap. Resume an existing conversation for this companyId if
	// one is cached; otherwise create a new one and cache the id for next
	// reload.
	React.useEffect(() => {
		const { companyId, contactId, phoneCallId, leadServedId } = args;
		if (!companyId) return;

		// Lead switched since the last bootstrap. Reset realtime state so the
		// new lead's cached / freshly-created conversation doesn't inherit
		// messages from the prior lead, then fall through to resume/createNew
		// in the same effect run. Returning early here (the prior behaviour)
		// left a render gap where the old conversation was still active.
		const isLeadSwitch = !!lastCompanyIdRef.current && lastCompanyIdRef.current !== companyId;
		if (isLeadSwitch) {
			realtime.reset();
			startedKeyRef.current = null;
		}

		lastCompanyIdRef.current = companyId;

		// When the bootstrap has already produced a conversation for *this*
		// lead, nothing to do. We deliberately skip this guard on lead-switch
		// because `realtime.reset()` above only schedules state changes — the
		// rendered `realtime.conversationId` still reflects the prior lead
		// until the next commit.
		if (!isLeadSwitch && (realtime.conversationId || realtime.isCreatingConversation)) return;

		const key = `${companyId}:${contactId ?? '-'}`;
		if (startedKeyRef.current === key) return;
		startedKeyRef.current = key;

		const cached = getCachedConversationId(companyId);
		if (cached) {
			realtime.resume(cached);
			return;
		}
		void realtime.createNew({ companyId, phoneCallId, leadServedId }).then(id => {
			if (id) setCachedConversationId(companyId, id);
		});
	}, [args, realtime]);

	// Tag-claim effect — see pins/README.md → "Why snapshots" for the full
	// rationale on the `skipIds` filter.
	React.useEffect(() => {
		if (pendingTagsRef.current.length === 0) return;

		let claimed = false;
		for (const msg of upstream) {
			if (pendingTagsRef.current.length === 0) break;
			if (msg.role !== 'assistant') continue;
			if (tagByIdRef.current.has(msg.id)) continue;
			const head = pendingTagsRef.current[0];
			if (head.skipIds.has(msg.id)) continue;
			pendingTagsRef.current.shift();
			tagByIdRef.current.set(msg.id, { pinnedKeyword: head.pinnedKeyword, pinColor: head.pinColor });
			claimed = true;
		}
		if (claimed) setTagVersion(v => v + 1);
	}, [upstream]);

	const messages = React.useMemo<CoachMessage[]>(
		() => upstream.map(m => toCoachMessage(m, tagByIdRef.current)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[upstream, tagVersion]
	);

	// Pull the next queued send and dispatch it, if nothing is currently in flight.
	// Auto-suggestion items can be no-ops if no transcript has accumulated; in
	// that case we release the lock immediately and try the next item rather
	// than wait for a stream-end transition that never comes.
	const drainSendQueue = React.useCallback(() => {
		if (inFlightRef.current) return;
		const next = sendQueueRef.current.shift();
		if (!next) return;
		inFlightRef.current = true;
		if (next.kind === 'direct') {
			if (next.tag) pendingTagsRef.current.push(next.tag);
			realtime.sendDirectQuestion(next.text);
			return;
		}
		if (next.kind === 'summary') {
			realtime.requestPostCallSummary();
			return;
		}
		// kind === 'autoSuggestion'
		const flushed = realtime.sendTranscriptSuggestion();
		if (flushed === 0) {
			inFlightRef.current = false;
			drainSendQueue();
		}
	}, [realtime]);

	// Watch the AI-responding flag. A true→false transition releases the
	// in-flight lock and dispatches the next queued send.
	const wasRespondingRef = React.useRef(false);
	React.useEffect(() => {
		const isResponding = realtime.isAIResponding;
		if (wasRespondingRef.current && !isResponding) {
			inFlightRef.current = false;
			drainSendQueue();
		}
		wasRespondingRef.current = isResponding;
	}, [realtime.isAIResponding, drainSendQueue]);

	const send = React.useCallback(
		(text: string, tag?: PendingKeywordTag) => {
			const trimmed = text.trim();
			if (!trimmed) return;
			let queuedTag: QueuedTag | undefined;
			if (tag) {
				const skipIds = new Set<string>();
				for (const m of upstreamRef.current) {
					if (m.role === 'assistant') skipIds.add(m.id);
				}
				queuedTag = { ...tag, skipIds };
			}
			sendQueueRef.current.push({ kind: 'direct', text: trimmed, tag: queuedTag });
			drainSendQueue();
		},
		[drainSendQueue]
	);

	const requestAutoSuggestion = React.useCallback(() => {
		// At most one suggestion can be queued at a time. If something else is
		// already queued or in flight (a direct send, or another suggestion),
		// the lead's continued speech keeps appending to `pendingTranscriptRef`
		// inside the realtime hook — when the queued suggestion finally fires,
		// it batches the entire accumulated transcript in one request rather
		// than triggering a second AI reply per utterance.
		const hasPendingSuggestion = sendQueueRef.current.some(item => item.kind === 'autoSuggestion');
		if (hasPendingSuggestion) return;
		sendQueueRef.current.push({ kind: 'autoSuggestion' });
		drainSendQueue();
	}, [drainSendQueue]);

	const startNewConversation = React.useCallback(() => {
		const { companyId } = args;
		if (companyId) clearCachedConversationId(companyId);
		// Drop send + tag queues so a stale before-call burst from the previous
		// conversation doesn't carry over into the fresh one.
		sendQueueRef.current = [];
		pendingTagsRef.current = [];
		tagByIdRef.current = new Map();
		inFlightRef.current = false;
		// Force the auto-bootstrap effect to run again.
		startedKeyRef.current = null;
		realtime.reset();
	}, [args, realtime]);

	// Internal helper: queue a post-call summary unless one is already queued
	// or in flight. Dedup so a flurry of clicks / repeated `call:ended` events
	// from Twilio (reconnects, glitches) only summarizes once.
	const queueSummary = React.useCallback(() => {
		const hasPendingSummary = sendQueueRef.current.some(item => item.kind === 'summary');
		if (hasPendingSummary) return;
		sendQueueRef.current.push({ kind: 'summary' });
		drainSendQueue();
	}, [drainSendQueue]);

	const endCall = React.useCallback(() => {
		// Only request the post-call summary if real transcription happened.
		// Without this guard, dial failures (e.g. Twilio gateway 31005 immediately
		// after `device.connect()`) trigger `call:ended` while the auto-Game-Plan
		// is still streaming, and the summary POST collides with the open stream
		// for a 409.
		const hasTranscript = upstreamRef.current.some(m => m.messageType === 'transcript');
		if (!hasTranscript) return;
		// Route through the send queue so the summary waits for any in-flight
		// stream (Game Plan, last auto-suggestion, etc.) to finish — Anthropic
		// 409s a concurrent POST to the same conversation, and the AI provider
		// 429s burst writes.
		queueSummary();
	}, [queueSummary]);

	// Public manual trigger for "Get summary". Skips the transcript guard
	// because the rep has explicitly clicked the button — we trust their
	// intent even if the transcript pipeline missed entries.
	const requestPostCallSummary = React.useCallback(() => {
		queueSummary();
	}, [queueSummary]);

	return {
		messages,
		isResponding: realtime.isAIResponding,
		isLoadingHistory: realtime.isLoadingHistory,
		wasResumed: realtime.wasResumed,
		conversationId: realtime.conversationId,
		send,
		addTranscriptEntry: realtime.addTranscriptEntry,
		requestAutoSuggestion,
		requestSuggestion: requestAutoSuggestion,
		requestPostCallSummary,
		startNewConversation,
		endCall,
	};
}
